import { Component } from '@angular/core';
import { BRAND } from 'src/app/data/brand/brand.data';
import { COMPANY_PAGES, RESOURCES_PAGES } from 'src/app/data/company/company.data';
import { PLUGINS, SERVICES, THEMES, COLOR_TOOL_KEYS, TOOLS } from 'src/app/data/product/product.data';
import { URLS } from '../../../data/navigation/navigation.data';
import { Product, ProductLine } from '../../../model/product/product.model';
import { ProductService } from '../../../service/product/product.service';

// The sitemap as the blog page (owner 2026-10-12): a chip per group in the hero, a row per group in the footer's
// order, its short card at the left and its pages as small cards with their icons at the right.
@Component( {
  selector: 'app-sitemap-page',
  templateUrl: './sitemap-page.component.html',
  styleUrls: [
    '../tool-pages/tools-page/tools-page.component.scss',
    '../plugin-pages/plugins-page/plugins-page.component.scss',
    '../blog-pages/blog-list-page/blog-list-page.component.scss',
    './sitemap-page.component.scss',
  ]
} )
export class SitemapPageComponent {

  // the footer's orders (footer.component.ts): the Mac and iPhone apps first, the services as the home's chips
  private static readonly APP_ORDER = [ 'macos-apps', 'ios-apps', 'plugins', 'jetbrains-plugins', 'vscode-extensions' ];
  private static readonly LEGAL_URLS = [ URLS.terms, URLS.storeTerms, URLS.privacyPolicy, URLS.cookiePolicy, URLS.sitemap ];

  readonly THEMES: ProductLine[] = THEMES;
  readonly PLUGINS: ProductLine[] = [ ...PLUGINS ].sort( ( a, b ) => SitemapPageComponent.rank( a.key ) - SitemapPageComponent.rank( b.key ) );
  readonly SERVICES: ProductLine[] = SERVICES;

  readonly ALL_TOOLS: Product[] = TOOLS[ 0 ]?.products ?? [];
  readonly TOOL_GROUPS: { name: string; tools: Product[] }[] = [
    { name: 'Color Tools', tools: this.ALL_TOOLS.filter( t => COLOR_TOOL_KEYS.includes( t.key ) ) },
    { name: 'Generators', tools: this.ALL_TOOLS.filter( t => !t.key.includes( '-to-' ) && !COLOR_TOOL_KEYS.includes( t.key ) ) },
    { name: 'Converters', tools: this.ALL_TOOLS.filter( t => t.key.includes( '-to-' ) ) },
  ];

  // Company with every resource that is a page of its own; the legal pages in their own list
  readonly COMPANY_LINKS = [ ...COMPANY_PAGES, ...RESOURCES_PAGES.filter( p => !SitemapPageComponent.LEGAL_URLS.includes( p.url ) ) ];
  // the legal pages; the sitemap itself is not one, and this page is it (owner 2026-10-12)
  readonly LEGAL_LINKS = RESOURCES_PAGES.filter( p => SitemapPageComponent.LEGAL_URLS.includes( p.url ) && p.url !== URLS.sitemap );

  readonly themeCount = THEMES.reduce( ( n, line ) => n + line.products.length, 0 );
  readonly pluginCount = PLUGINS.reduce( ( n, line ) => n + line.products.length, 0 );

  // the rows in the footer's order (owner 2026-10-12), each with its short card's words
  readonly GROUPS: { key: string; name: string; eyebrow: string; lead: string; link?: string; count: number; unit: string; units: string }[] = [
    { key: 'apps', name: 'Apps & Plugins', eyebrow: 'Install', lead: 'Mac and iPhone apps, browser and IDE plugins',
      link: URLS.plugins.plugins, count: this.pluginCount, unit: 'App', units: 'Apps' },
    { key: 'themes', name: 'Themes', eyebrow: 'Style', lead: 'Color themes inspired by nature and the cities we love',
      link: URLS.themes, count: this.themeCount, unit: 'Theme', units: 'Themes' },
    { key: 'tools', name: 'Tools', eyebrow: 'Create', lead: 'Free color tools, generators and converters in your browser',
      link: URLS.tools.tools, count: this.ALL_TOOLS.length, unit: 'Tool', units: 'Tools' },
    { key: 'services', name: 'Services', eyebrow: 'Build', lead: 'Software built and integrated for your business',
      count: SERVICES.reduce( ( n, line ) => n + line.products.length, 0 ), unit: 'Service', units: 'Services' },
    { key: 'company', name: 'Company & Resources', eyebrow: 'About', lead: 'Who we are, what we write and what we share',
      count: this.COMPANY_LINKS.length, unit: 'Page', units: 'Pages' },
    { key: 'legal', name: 'Legal', eyebrow: 'Read', lead: 'The terms and policies of the site and the store',
      count: this.LEGAL_LINKS.length, unit: 'Page', units: 'Pages' },
  ];

  URLS = URLS;
  BRAND = BRAND;

  constructor( public productService: ProductService ) {
  }

  private static rank( key: string ): number {
    const i = SitemapPageComponent.APP_ORDER.indexOf( key );
    return i < 0 ? SitemapPageComponent.APP_ORDER.length : i;
  }

  // Real, crawlable href for each product link (SEO + a11y); navigation is still handled in JS so the SPA
  // fragment-scroll / external-open behaviour is preserved.
  productHref( product: Product, productLine: ProductLine ): string {
    return this.productService.productUrl( product, productLine );
  }

  redirectToProductUrl( product: Product, productLine: ProductLine, event?: Event ) {
    event?.preventDefault();
    this.productService.redirectToProductUrl( product, productLine );
  }

  /** a group chip: scroll to the group (the app scrolls on <body>, where a smooth scrollIntoView does nothing) */
  jumpTo( key: string, event?: Event ): void {
    event?.preventDefault();
    const el = document.getElementById( 'sm-' + key );
    if ( !el ) { return; }
    const scroller = document.body.scrollHeight > document.body.clientHeight + 4 ? document.body : document.documentElement;
    const offset = window.matchMedia( '(max-width: 1023px)' ).matches ? 70 : 96;
    const from = scroller.scrollTop;
    const to = Math.max( 0, el.getBoundingClientRect().top + from - offset );
    const start = performance.now();
    const step = ( now: number ) => {
      const t = Math.min( 1, ( now - start ) / 420 );
      scroller.scrollTop = from + ( to - from ) * ( 1 - Math.pow( 1 - t, 3 ) );
      if ( t < 1 ) { requestAnimationFrame( step ); }
    };
    requestAnimationFrame( step );
  }
}
